import React, {useState, useEffect} from 'react';
import {Button} from 'react95';
import WindowInvitation from './WindowInvitation';
import WindowRSVP from './WindowRSVP';
import WindowAbout from './WindowAbout';
import WindowWishlist from './WindowWishlist';
import WindowGuestbook from './WindowGuestbook';
import WindowTimer from './WindowTimer';
import WindowWelcome from './WindowWelcome';
import WindowEasterEggs from './WindowEasterEggs';
import {AuthUser} from '../services/authService';
import styles from '../styles/modules/Desktop.module.css';

interface Icon {
  id: string;
  name: string;
  icon: string;
  component: React.ComponentType<{onClose: () => void}>;
}

interface DesktopProps {
  onLogout: () => void;
  currentUser: AuthUser | null;
  onOpenWindow: (window: string) => void;
}

// Новый компонент для иконки
const DesktopIcon = ({icon, label, onClick, selected}) => (
  <div
    className={styles.desktopIcon + (selected ? ' ' + styles.selected : '')}
    tabIndex={0}
    onClick={onClick}
    onFocus={onClick}
  >
    <div className={styles.iconImage}>{icon}</div>
    <div className={styles.desktopIconLabel}>{label}</div>
  </div>
);

// Компонент для декоративных элементов
const DecorativeIcon = ({icon, label, style}) => (
  <div
    className={styles.decorativeIcon}
    style={style}
  >
    <div className={styles.iconImage}>{icon}</div>
    <div className={styles.desktopIconLabel}>{label}</div>
  </div>
);

// Компонент для винтажных элементов
const VintageElement = ({children, style}) => (
  <div className={styles.vintageElement} style={style}>
    {children}
  </div>
);

// Компонент для цитат
const Quote = ({children, style}) => (
  <div className={styles.quote} style={style}>
    {children}
  </div>
);

const Desktop: React.FC<DesktopProps> = ({onLogout, currentUser, onOpenWindow}) => {
  const [openWindows, setOpenWindows] = useState<string[]>([]);
  const [activeWindow, setActiveWindow] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showStartMenu, setShowStartMenu] = useState(false);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  const handleDontShowAgain = (dontShow: boolean) => {
    if (dontShow) {
      localStorage.setItem('hasSeenWelcome', 'true');
    }
  };

  const icons: Icon[] = [
    {
      id: 'invitation',
      name: 'Расписание',
      icon: '📋',
      component: WindowInvitation,
    },
    {
      id: 'rsvp',
      name: 'Подтверждение присутствия',
      icon: '✅',
      component: WindowRSVP,
    },
    {
      id: 'wishlist',
      name: 'Подарки',
      icon: '🎁',
      component: WindowWishlist,
    },
    {
      id: 'guestbook',
      name: 'Гостевая книга',
      icon: '📖',
      component: WindowGuestbook,
    },
    // Hidden easter egg
    {
      id: 'eastereggs',
      name: 'Системные утилиты',
      icon: '🎯',
      component: WindowEasterEggs,
    }
  ];

  const handleIconClick = (id: string) => {
    if (!openWindows.includes(id)) {
      setOpenWindows([...openWindows, id]);
    }
    setActiveWindow(id);
    setSelectedIdx(icons.findIndex(i => i.id === id));
  };

  const handleCloseWindow = (id: string) => {
    setOpenWindows(openWindows.filter(windowId => windowId !== id));
    if (activeWindow === id) {
      setActiveWindow(openWindows.filter(windowId => windowId !== id)[0] || null);
    }
    setSelectedIdx(null);
  };

  const handleWindowClick = (id: string) => {
    if (activeWindow !== id) {
      setActiveWindow(id);
    }
  };

  const handleStartClick = () => {
    setShowStartMenu(!showStartMenu);
  };

  const handleStartMenuClick = (action: string) => {
    setShowStartMenu(false);
    
    if (action === 'easter') {
      handleIconClick('eastereggs');
    } else if (action === 'programs') {
      alert('📁 Программы:\n\n• Сапёр (опасно для жизни)\n• Блокнот (для важных записей)\n• Калькулятор (для подсчёта лет)\n• Paint (для рисования будущего)\n• Пасьянс "Косынка" (главная программа 90х)\n• ICQ (ищу девушку для серьёзных отношений)\n• WinRAR (40 дней пробной версии с 1995 года)');
    } else if (action === 'documents') {
      alert('📄 Документы:\n\n• Резюме_1995.doc\n• Список_желаний_на_30_лет.txt\n• Секретные_коды_от_игр.txt\n• Инструкция_по_настройке_модема.rtf\n• Фотки_с_вечеринок.zip\n• Дискеты_с_музыкой.rar\n• Backup_всей_жизни.zip (повреждён)');
    } else if (action === 'settings') {
      alert('⚙️ Настройки:\n\n• Разрешение: 800x600 (максимум!)\n• Цвета: 256 (роскошь)\n• Звук: SoundBlaster совместимый\n• Сеть: Dial-up 56k (быстрее некуда)\n• Мышь: PS/2 с шариком\n• Заставка: Летающие тосты\n• Время: всегда показывает 23:59\n• Автозапуск CD: ВКЛЮЧЕН (опасно)');
    }
  };

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showStartMenu) {
        const startMenu = document.querySelector(`.${styles.startMenu}`);
        const startButton = document.querySelector(`.${styles.startButton}`);
        
        if (startMenu && startButton && 
            !startMenu.contains(event.target as Node) && 
            !startButton.contains(event.target as Node)) {
          setShowStartMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStartMenu]);

  return (
    <div className={styles.desktopRoot}>
      <div className={styles.desktopContainer}>
        <div className={styles.desktop}>
          <div className={styles.iconGrid}>
            {icons.filter(item => item.id !== 'eastereggs').map((item, idx) => (
              <DesktopIcon
                key={item.id}
                icon={item.icon}
                label={item.name}
                selected={selectedIdx === idx}
                onClick={() => handleIconClick(item.id)}
              />
            ))}
          </div>

          {/* Декоративные элементы */}
          <div className={styles.decorativeElements}>
            {/* Статичные иконки-декорации */}
            <DecorativeIcon
              icon="🖥️"
              label="Мой компьютер"
              style={{position: 'absolute', top: '120px', right: '80px'}}
            />
            <DecorativeIcon
              icon="📄"
              label="C:\жизнь\новый_этап.txt"
              style={{position: 'absolute', top: '220px', right: '60px'}}
            />
            <DecorativeIcon
              icon="⚠️"
              label="НЕ_ОТКРЫВАТЬ.bat"
              style={{position: 'absolute', top: '320px', right: '100px'}}
            />
            <DecorativeIcon
              icon="⚙️"
              label="AutoLaunchParty.reg"
              style={{position: 'absolute', top: '420px', right: '40px'}}
            />
            <DecorativeIcon
              icon="📜"
              label="30_years_old.vbs"
              style={{position: 'absolute', top: '520px', right: '90px'}}
            />

            {/* Винтажные элементы */}
            <VintageElement style={{position: 'absolute', top: '100px', left: '55%'}}>
              <div className={`${styles.casualPhoto} ${styles.rotate1}`}>
                <img src="/images/1.jpg" alt="Детство" />
              </div>
            </VintageElement>

            <VintageElement style={{position: 'absolute', top: '300px', left: '70%'}}>
              <div className={styles.diskette}>
                <div className={styles.disketteIcon}>💾</div>
                <div className={styles.disketteLabel}>Backup_1995.A</div>
              </div>
            </VintageElement>

            <VintageElement style={{position: 'absolute', top: '480px', left: '55%'}}>
              <div className={styles.note}>
                <div className={styles.noteText}>📝 Вернусь в 2003</div>
              </div>
            </VintageElement>

            {/* Дополнительные фотки */}
            <VintageElement style={{position: 'absolute', top: '220px', left: '45%'}}>
              <div className={`${styles.casualPhoto} ${styles.rotate2}`}>
                <img src="/images/2.jpg" alt="Школьные годы" />
              </div>
            </VintageElement>

            <VintageElement style={{position: 'absolute', top: '380px', left: '70%'}}>
              <div className={`${styles.casualPhoto} ${styles.rotate3}`}>
                <img src="/images/3.jpeg" alt="Подростковые годы" />
              </div>
            </VintageElement>

            <VintageElement style={{position: 'absolute', top: '140px', left: '70%'}}>
              <div className={`${styles.casualPhoto} ${styles.rotate4}`}>
                <img src="/images/4.jpeg" alt="Старое фото" />
              </div>
            </VintageElement>

            {/* Цитаты и файловые шутки */}
            <Quote style={{position: 'absolute', bottom: '200px', left: '40px'}}>
              <div className={styles.commandLine}>
                <div className={styles.prompt}>C:\&gt;</div>
                <div className={styles.command}>echo Happy 30s, бро! &gt; C:\жизнь\новый_этап.txt</div>
              </div>
            </Quote>

            <Quote style={{position: 'absolute', top: '180px', left: '30%'}}>
              <div className={styles.fileComment}>
                <div className={styles.commentPrefix}>//</div>
                <div className={styles.commentText}>C:\Users\Влад\30-летие\nostalgia.txt</div>
              </div>
            </Quote>

            <Quote style={{position: 'absolute', bottom: '160px', right: '50px'}}>
              <div className={styles.nostalgiaQuote}>
                "Помнишь, как слушали Руки Вверх<br/>
                на WinAmp и ели Роллтон?"
              </div>
            </Quote>
          </div>

          {/* Render welcome window */}
          {showWelcome && <WindowWelcome onClose={handleWelcomeClose} onDontShowAgain={handleDontShowAgain} />}

          {/* Render open windows */}
          {openWindows.map(windowId => {
            const icon = icons.find(i => i.id === windowId);
            if (!icon) return null;

            const WindowComponent = icon.component;
            const windowProps: any = {onClose: () => handleCloseWindow(windowId)};

            // Add special props for specific windows
            if (windowId === 'invitation') {
              windowProps.onOpenRSVP = () => handleIconClick('rsvp');
            }

            return (
              <div
                key={windowId}
                className={`${styles.windowContainer} ${activeWindow === windowId ? styles.activeWindow : ''}`}
                onClick={() => handleWindowClick(windowId)}
              >
                <WindowComponent {...windowProps} />
              </div>
            );
          })}
        </div>
        
        {/* Taskbar */}
        <div className={styles.taskBar}>
          <div style={{position: 'relative'}}>
            <Button 
              className={`${styles.startButton} ${showStartMenu ? styles.active : ''}`}
              onClick={handleStartClick}
            >
              <span>▶️</span>
              <span>Пуск</span>
            </Button>
            
            {showStartMenu && (
              <div className={styles.startMenu}>
                <div className={styles.startMenuHeader}>
                  Windows 95
                </div>
                <div className={styles.startMenuItems}>
                  <div className={styles.startMenuItem} onClick={() => handleStartMenuClick('programs')}>
                    <span>📁</span>
                    Программы
                  </div>
                  <div className={styles.startMenuItem} onClick={() => handleStartMenuClick('documents')}>
                    <span>📄</span>
                    Документы
                  </div>
                  <div className={styles.startMenuItem} onClick={() => handleStartMenuClick('settings')}>
                    <span>⚙️</span>
                    Настройки
                  </div>
                  <div className={styles.startMenuItem} onClick={() => handleStartMenuClick('easter')}>
                    <span>🔧</span>
                    Системные утилиты
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Desktop;
